"use client";

import { useMemo, useState, useEffect } from "react";
import { getConstellationNodes, getConstellationEdges } from "@/lib/atlas/constellationLayout";

export interface SynapseLinesProps {
  nodeCount: number;
  primaryColor: string;
  secondaryColor: string;
  phase: "sync" | "collapse";
  collapseProgress?: number;
  reducedMotion?: boolean;
  className?: string;
}

/**
 * Lines between constellation nodes; 2 energy pulses for a more alive feel.
 */
export default function SynapseLines({
  nodeCount,
  primaryColor,
  secondaryColor,
  phase,
  collapseProgress = 0,
  reducedMotion = false,
  className = "",
}: SynapseLinesProps) {
  const nodes = useMemo(() => getConstellationNodes(nodeCount), [nodeCount]);
  const edges = useMemo(() => getConstellationEdges(nodes), [nodes]);

  const [pulseA, setPulseA] = useState(0);
  const [pulseB, setPulseB] = useState(1);
  const uid = useMemo(() => `atlas-${Math.random().toString(36).slice(2, 9)}`, []);

  useEffect(() => {
    if (reducedMotion || phase !== "sync" || edges.length === 0) return;
    const advance = () => {
      setPulseA((a) => (a + 1) % edges.length);
      setPulseB((b) => (b + 1) % edges.length);
    };
    const id = setInterval(advance, 1100);
    return () => clearInterval(id);
  }, [phase, reducedMotion, edges.length]);

  if (reducedMotion || phase === "collapse") return null;

  const viewBox = "0 0 100 100";
  const paths = edges.map(([a, b]) => {
    const n1 = nodes[a];
    const n2 = nodes[b];
    if (!n1 || !n2) return "";
    return `M ${n1.x} ${n1.y} L ${n2.x} ${n2.y}`;
  });

  return (
    <svg
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      viewBox={viewBox}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${uid}-line`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={primaryColor} stopOpacity="0.35" />
          <stop offset="50%" stopColor={secondaryColor} stopOpacity="0.6" />
          <stop offset="100%" stopColor={primaryColor} stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id={`${uid}-pulse`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={primaryColor} stopOpacity="0.4" />
          <stop offset="50%" stopColor={secondaryColor} stopOpacity="1" />
          <stop offset="100%" stopColor={primaryColor} stopOpacity="0.4" />
        </linearGradient>
      </defs>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke={`url(#${uid}-line)`}
          strokeWidth="0.45"
          strokeOpacity="0.45"
          style={{
            strokeDasharray: 100,
            strokeDashoffset: phase === "collapse" ? 100 * (collapseProgress ?? 0) : 0,
            transition: "stroke-dashoffset 0.25s ease-out",
          }}
        />
      ))}
      {paths[pulseA] && (
        <path
          d={paths[pulseA]}
          fill="none"
          stroke={`url(#${uid}-pulse)`}
          strokeWidth="1.4"
          strokeOpacity="1"
          style={{
            strokeDasharray: "8 92",
            animation: "atlas-pulse-dash 1.3s ease-in-out infinite",
          }}
        />
      )}
      {paths[pulseB] && pulseB !== pulseA && (
        <path
          d={paths[pulseB]}
          fill="none"
          stroke={`url(#${uid}-pulse)`}
          strokeWidth="1.2"
          strokeOpacity="0.9"
          style={{
            strokeDasharray: "8 92",
            animation: "atlas-pulse-dash 1.5s ease-in-out infinite 0.4s",
          }}
        />
      )}
    </svg>
  );
}
